import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { clearAuthTokens } from '../utils/api';
import { prefetch, clearCache } from '../utils/apiCache';
import queryClient from '../utils/queryClient';
import i18n from '../i18n';
import { getBrand } from '../utils/brand';

const AuthContext = createContext(null);

function getInitialTheme() {
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(getInitialTheme);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatorUser, setImpersonatorUser] = useState(null);

  // Apply theme to DOM and listen for OS changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    // Only follow OS changes if user hasn't manually chosen
    if (localStorage.getItem('theme')) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', next);
      return next;
    });
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user || res.data);
      i18n.changeLanguage((res.data.user || res.data).language || 'es');
      if (res.data.impersonating) {
        setIsImpersonating(true);
        setImpersonatorUser(res.data.impersonator);
      }
      // Warm cache for common dropdowns while user sees the first page
      prefetch(['/companies?limit=500']);
    } catch {
      // If we have stashed admin tokens, restore them instead of logging out
      const stashedToken = sessionStorage.getItem('admin_accessToken');
      if (stashedToken) {
        localStorage.setItem('accessToken', stashedToken);
        localStorage.setItem('refreshToken', sessionStorage.getItem('admin_refreshToken') || '');
        sessionStorage.removeItem('admin_accessToken');
        sessionStorage.removeItem('admin_refreshToken');
        setIsImpersonating(false);
        setImpersonatorUser(null);
        // Retry loading with admin token
        try {
          const res2 = await api.get('/auth/me');
          setUser(res2.data.user || res2.data);
          i18n.changeLanguage((res2.data.user || res2.data).language || 'es');
        } catch {
          clearAuthTokens();
        }
      } else {
        clearAuthTokens();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const playLoginChime = () => {
    try {
      const audio = new Audio('/sounds/win95-startup.wav');
      // 70% del volumen del equipo: a tope aturdía (Andy, 10-oct-2026).
      audio.volume = 0.7;
      const promise = audio.play();
      if (promise) promise.catch(() => {
        // Browser blocked autoplay (e.g. Google redirect login) — play on first interaction
        const handler = () => {
          audio.play().catch(() => {});
          document.removeEventListener('click', handler);
          document.removeEventListener('keydown', handler);
        };
        document.addEventListener('click', handler, { once: true });
        document.addEventListener('keydown', handler, { once: true });
      });
    } catch {}
  };

  const login = async (email, password, totpCode) => {
    const res = await api.post('/auth/login', { email, password, ...(totpCode ? { totpCode } : {}) });
    if (res.data.requires2FA) return res.data;
    const { accessToken, refreshToken, user: userData } = res.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setUser(userData);
    i18n.changeLanguage(userData.language || 'es');
    if (userData.loginSoundEnabled !== false) playLoginChime();
    return userData;
  };

  // Used by Google OAuth flow
  const setAuthData = ({ accessToken, refreshToken, user: userData }) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setUser(userData);
    i18n.changeLanguage(userData.language || 'es');
    if (userData.loginSoundEnabled !== false) playLoginChime();
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try { await api.post('/auth/logout', { refreshToken }); } catch {}
    clearAuthTokens();
    clearCache();
    setUser(null);
  };

  const updateLanguage = async (lang) => {
    await api.post('/auth/language', { language: lang });
    setUser(prev => ({ ...prev, language: lang }));
    i18n.changeLanguage(lang);
  };

  const startImpersonation = async (targetUserId) => {
    if (isImpersonating) return; // prevent nested impersonation
    try {
      const res = await api.post(`/admin/impersonate/${targetUserId}`);
      // Stash current admin tokens
      sessionStorage.setItem('admin_accessToken', localStorage.getItem('accessToken'));
      sessionStorage.setItem('admin_refreshToken', localStorage.getItem('refreshToken'));
      // Set impersonation token (no refresh token)
      localStorage.setItem('accessToken', res.data.token);
      localStorage.removeItem('refreshToken');
      setUser(res.data.user);
      setIsImpersonating(true);
      setImpersonatorUser({ id: user.id, email: user.email });
      queryClient.clear(); // flush all cached data from admin session
      i18n.changeLanguage(res.data.user.language || 'es');
    } catch (err) {
      throw err;
    }
  };

  const stopImpersonation = async () => {
    const targetId = user?.id;
    // Restore admin tokens
    const adminAccess = sessionStorage.getItem('admin_accessToken');
    const adminRefresh = sessionStorage.getItem('admin_refreshToken');
    if (adminAccess) {
      localStorage.setItem('accessToken', adminAccess);
      if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);
    }
    sessionStorage.removeItem('admin_accessToken');
    sessionStorage.removeItem('admin_refreshToken');
    setIsImpersonating(false);
    setImpersonatorUser(null);
    queryClient.clear(); // flush cached data from impersonated session
    // Log stop (best-effort, now using admin token)
    try { await api.post('/admin/impersonate/stop', { targetId }); } catch {}
    // Reload admin user
    await loadUser();
  };

  const isAdmin = user?.role === 'admin';
  const isDemo = user?.role === 'demo';
  const isManager = ['admin', 'manager'].includes(user?.role);
  const isScoped = !!user?.companyScope;
  const isClient = user?.role === 'client' || !!user?.isClient;
  // Can this user turn company access on/off for teammates? Admins/managers always;
  // trusted agents via the can_grant_access flag (without HR/financial exposure).
  const canGrantAccess = isManager || !!user?.canGrantAccess;

  const hasModule = useCallback((key) => {
    // La INSTANCIA manda sobre el rol. Un admin saltea el permiso por usuario —así está
    // diseñado— pero un módulo que no forma parte de esta instancia no existe para nadie,
    // ni para él. Sin esto, el admin de un colegio veía la burbuja de WhatsApp, el widget
    // de VPS Health y cualquier otra herramienta interna nuestra. Mismo criterio y mismo
    // orden que requireModule() en backend/src/middleware/auth.js.
    const inst = getBrand()?.modules;
    if (inst && key) {
      const limpia = String(key).replace(/^group_/, '');
      if (!inst.includes(key) && !inst.includes(limpia)) return false;
    }
    if (isAdmin || isDemo) return true;
    // Role-based defaults — MUST stay in sync, key for key, with getModuleDefaults() in
    // backend/src/middleware/auth.js. The two maps are independent copies: the backend one
    // decides whether the API answers, this one decides whether the sidebar/nav shows the
    // item. A key present there but MISSING here resolves to `!!undefined` = false, so the
    // module is reachable by URL and blocked from the menu — invisible for every agent who
    // doesn't have it set explicitly. That's how `workshop` (default true on the backend)
    // never showed up for Gustavo, along with 18 other keys that had drifted (ago-2026).
    // When you add a module, add it to BOTH maps in the same commit.
    const defaults = {
      comms_monitor: false,
      agent_status: isManager && !isScoped,
      hr: isManager && !isScoped,
      remote_access: true,
      quick_launch: true,
      voice_calls: true,
      cctv: false,
      '2fa_codes': true,
      settings: false,
      chat: true,
      whatsapp: true,
      phone: false,
      knowledge_base: true,
      budgets: false,
      vault: false,
      audit_log: isManager,
      deployments: true,
      calendar: true,
      errands: true,
      requests: true,
      cloud_costs: false,
      google_business: false,
      legacy_sheets: false,
      web_review: false,
      reports: false,
      diagnostics: isManager,
      assets: true,
      workshop: true,
      access_control: false,
      file_permissions: false,
      email_accounts: true,
      monitoring: false,
      backup_monitor: false,
      ops_monitor: false,
      vps_health: false,
      dmarc: false,
      ip_intel: isManager,
      vpn: false,
      projects: true,
      rag_search: true,
      ai_assist: true,
      feedback_requests: true,
      changelog: true,
      personnel: true,
      topology: true,
      procedures: true,
      provisioning: true,
      resource_monitor: false,
      game: true,
      prode: false, // Mundial terminado (jul-2026) — módulo desactivado
      nl_command: true,
      prepaid_lines: false,
      racks: true,
      mail_peek: false,
      live_assist_pro: false,
      mercadolibre: false,
    };
    const mods = user?.modules;
    if (mods && Object.keys(mods).length > 0) {
      // Explicit setting takes priority, otherwise fall back to default
      if (key in mods) return mods[key] === true;
      return !!defaults[key];
    }
    return !!defaults[key];
  }, [user, isAdmin, isDemo, isManager, isScoped]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateLanguage, setAuthData, isAdmin, isDemo, isManager, isScoped, isClient, canGrantAccess, hasModule, loadUser, theme, toggleTheme, isImpersonating, impersonatorUser, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
